import { Server, Cloud, Phone, ShieldCheck, EyeOff, Lock } from "lucide-react";
import { T } from "../theme";
import { Eyebrow, Card } from "../components";
import type { ProfileConfig } from "../api/config";

/** PRIVACY — live data from config. Shows actual redaction level + data residency. */
export function Privacy({ config }: { config: ProfileConfig }) {
  const levels = ["open", "initials", "opaque"] as const;
  const currentIdx = levels.indexOf(config.redactionLevel);

  const rows = [
    { icon: Server, where: "Zoidberg box (home)", what: "Calendar, tasks, history, OAuth tokens", tone: T.mint },
    { icon: Cloud, where: "Cloudflare edge", what: "Config, events, tasks · encrypted at rest", tone: T.gold },
    { icon: Phone, where: "Twilio + ElevenLabs", what: "Spoken audio only, at call time · no training", tone: T.dawn },
  ];

  return (
    <div style={{ padding: "18px 16px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Eyebrow>privacy</Eyebrow>
        <ShieldCheck size={14} color={T.mint} />
      </div>

      <div style={{ margin: "16px 0 14px", fontSize: 20, color: T.paper, fontWeight: 650 }}>Where your day lives</div>

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {rows.map((r) => (
          <Card key={r.where} style={{ display: "flex", gap: 11, alignItems: "flex-start", borderLeft: `3px solid ${r.tone}` }}>
            <r.icon size={15} color={r.tone} style={{ marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 13, color: T.paper, fontWeight: 600 }}>{r.where}</div>
              <div style={{ fontSize: 11.5, color: T.mist, marginTop: 2, lineHeight: 1.45 }}>{r.what}</div>
            </div>
          </Card>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <Eyebrow color={T.gold}>what calls say out loud</Eyebrow>
        <div style={{ display: "flex", gap: 6, marginTop: 9 }}>
          {levels.map((lvl, i) => (
            <div
              key={lvl}
              style={{
                flex: 1,
                textAlign: "center",
                padding: "8px 0",
                borderRadius: 10,
                fontSize: 11.5,
                fontWeight: 600,
                background: i === currentIdx ? "rgba(255,180,84,.16)" : T.panel,
                border: `1px solid ${i === currentIdx ? T.gold : T.edge}`,
                color: i === currentIdx ? T.gold : T.dim,
                textTransform: "capitalize",
              }}
            >
              {lvl}
            </div>
          ))}
        </div>
        <Card style={{ marginTop: 9, padding: "10px 13px" }}>
          <div className="mono" style={{ fontSize: 10.5, color: T.mist, lineHeight: 1.6 }}>
            <EyeOff size={11} style={{ verticalAlign: "-1.5px", marginRight: 5 }} color={T.gold} />
            Current: <span style={{ color: T.gold, fontWeight: 600 }}>{config.redactionLevel}</span> —
            {config.redactionLevel === "open"
              ? " full names spoken. Only for testing."
              : config.redactionLevel === "initials"
              ? " names缩减 to initials. 'R.' not 'Richard'."
              : " completely opaque. 'a meeting' not 'ESG meeting'."}
          </div>
        </Card>
      </div>

      <Card style={{ marginTop: 12, display: "flex", gap: 9, alignItems: "center" }}>
        <Lock size={13} color={T.mint} />
        <span style={{ fontSize: 11.5, color: T.mist }}>
          Phone: <span style={{ color: T.paper, fontWeight: 600 }}>{config.phones.primary}</span> ·
          backups: {config.phones.backups.length}
        </span>
      </Card>
    </div>
  );
}
